"use client";

import { signOut } from "next-auth/react";

/**
 * Recover from a DEAD session — a `authjs.session-token` cookie that still
 * decrypts (so `useSession` reports "authenticated" and the middleware's
 * cookie-existence gate lets pages through) but whose Cognito refresh token is
 * expired/revoked, so the `jwt` callback stamps `error: "RefreshAccessTokenError"`
 * and every API route 401s. Nothing else ever clears that cookie, so the user
 * refreshes forever and sees a wall of "Python charts api error: undefined".
 *
 * Mirrors SH's `ShareholderSessionProvider`: drop the cookie, then hard-navigate
 * to /login (which re-runs Cognito sign-in — silently if the hosted-UI session
 * is still alive, otherwise the login form). A hard navigation, not a router
 * push, so all module-level state is reset. Idempotent within a page: many
 * chart cells 401 at once and must not race a dozen sign-outs.
 */
let recovering = false;

export function isDeadSessionError(error: unknown): boolean {
  return error === "RefreshAccessTokenError";
}

export async function recoverDeadSession(reason: string): Promise<void> {
  if (typeof window === "undefined" || recovering) return;
  if (window.location.pathname.startsWith("/login")) return;
  recovering = true;
  console.warn(`[auth] session is dead (${reason}) — signing out and re-authenticating`);

  const { pathname, search } = window.location;
  const callbackUrl = encodeURIComponent(pathname + search);
  try {
    // Clears the (apex-scoped when deployed) session cookie server-side.
    await signOut({ redirect: false });
  } catch (err) {
    console.error("[auth] signOut during session recovery failed:", err);
  }
  window.location.href = `/login?callbackUrl=${callbackUrl}`;
}
